import { useEffect, useRef } from "react";
import { AlertCircle } from "lucide-react";
import { ApiError } from "@/api/client";
import { getUserMessage, getFieldPathLabel, humanizeFieldMessage } from "@/lib/errorMessages";

interface ErrorBannerProps {
  error: Error | null | undefined;
}

export default function ErrorBanner({ error }: ErrorBannerProps) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (error && ref.current) {
      ref.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
    if (error instanceof ApiError) {
      console.warn("[API Error]", {
        status: error.status,
        code: error.code,
        message: error.message,
        fieldErrors: error.fieldErrors,
      });
    }
  }, [error]);

  if (!error) return null;

  const message = getUserMessage(error);
  const fieldErrors =
    error instanceof ApiError && error.fieldErrors ? error.fieldErrors : undefined;
  // Whole-object rules (`.refine()`) fail with no path at all; without this line their reason
  // never reaches the screen and the save looks rejected for no stated cause.
  const formErrors = error instanceof ApiError ? (error.formErrors ?? []) : [];

  return (
    <div
      ref={ref}
      role="alert"
      className="bg-error-50 dark:bg-error-900/30 rounded-lg border border-red-200 p-4 dark:border-red-800/50"
    >
      <div className="flex items-start gap-3">
        <AlertCircle className="text-error-600 dark:text-error-400 mt-0.5 h-5 w-5 shrink-0" />
        <div className="min-w-0">
          <p className="text-error-700 dark:text-error-400 font-medium">{message}</p>
          {(formErrors.length > 0 || (fieldErrors && Object.keys(fieldErrors).length > 0)) && (
            <ul className="text-error-600 dark:text-error-400/80 mt-2 space-y-1 text-sm">
              {formErrors.map((m) => (
                <li key={m}>{humanizeFieldMessage(m)}</li>
              ))}
              {Object.entries(fieldErrors ?? {}).map(([field, messages]) => (
                <li key={field}>
                  <span className="font-medium">{getFieldPathLabel(field)}:</span>{" "}
                  {humanizeFieldMessage(messages[0])}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
